import React, { useEffect, useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  FaCog,
  FaUserTie,
  FaBuilding,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaPercentage,
  FaCheckCircle,
  FaPlus,
} from "react-icons/fa";
import {
  FiEdit2,
  FiX,
  FiSearch,
  FiUser,
  FiBriefcase,
  FiRefreshCw,
  FiPhone,
} from "react-icons/fi";
import Swal from "sweetalert2";

import {
  postData,
  getData,
} from "../../services/FetchNodeservices";

export default function UserAllJoining() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [filterCompany, setFilterCompany] = useState("");
  const [filterPosition, setFilterPosition] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [columns, setColumns] = useState({
    candidate: true,
    company: true,
    position: true,
    recruiter: true,
    doj: true,
    ctc: true,
    revenue: true,
    status: true,
  });
  const [openColumns, setOpenColumns] = useState(false);
  const columnRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (columnRef.current && !columnRef.current.contains(e.target)) {
        setOpenColumns(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ================================================= */
  /* FORM STATE */
  /* ================================================= */

  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const emptyFormData = {
    s_no: null,
    candidate_id: null,
    candidate_name: "",
    phone: "",
    company: "",
    position: "",
    designation: "",
    doj: dayjs().format("YYYY-MM-DD"),
    month: dayjs().format("MMMM"),
    ctc_offered: "",
    percentage: "8.33",
    amount: "",
    status_joined: "Joined",
    entered_by: "",
    user_id: null,
  };

  const [formData, setFormData] = useState({ ...emptyFormData });
  const [isManualAmount, setIsManualAmount] = useState(false);

  /* ================================================= */
  /* ACCURATE AMOUNT CALCULATION HELPER */
  /* ================================================= */

  const getAccurateAmount = (r) => {
    const ctc = Number(r.ctc_offered) || 0;
    const pct = Number(r.percentage) || 8.33;
    const amt = Number(r.amount);

    // If stored amount is suspicious (like 0.67 on a 6.48 Lakh CTC) or zero while CTC is set
    if (ctc > 0 && (!amt || (amt < 100 && ctc > 1000) || Math.abs(amt - (ctc * pct) / 100) > 100)) {
      return Number(((ctc * pct) / 100).toFixed(2));
    }
    return amt || Number(((ctc * pct) / 100).toFixed(2));
  };

  const formatINR = (val) => {
    const num = Number(val) || 0;
    return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  };

  /* ================================================= */
  /* FETCH */
  /* ================================================= */

  const fetchAll = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem("USER") || "{}");
      const adminData = JSON.parse(localStorage.getItem("ADMIN") || "{}");
      const user_id = userData?.id || adminData?.id;

      if (!user_id) {
        console.error("User ID not found");
        setRows([]);
        return;
      }

      const res = await getData(`placementtracker/fetch/${user_id}`);
      if (res?.status) {
        setRows(res.data || []);
      } else {
        console.error("Fetch failed:", res?.message);
        setRows([]);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  /* ================================================= */
  /* OPTIONS */
  /* ================================================= */

  const companyOptions = useMemo(
    () => [
      ...new Set(
        rows
          .map((r) => r.company)
          .filter(Boolean)
      ),
    ],
    [rows]
  );

  const positionOptions = useMemo(
    () => [
      ...new Set(
        rows
          .map((r) => r.position)
          .filter(Boolean)
      ),
    ],
    [rows]
  );

  const statusOptions = [
    "Joined",
    "Offered",
    "Joining Pending",
    "Backout",
    "Rejected",
  ];

  const standardPercentages = [
    { label: "7%", value: 7 },
    { label: "7.33%", value: 7.33 },
    { label: "8%", value: 8 },
    { label: "8.33% (1 Month)", value: 8.33 },
    { label: "8.5%", value: 8.5 },
    { label: "10%", value: 10 },
  ];

  /* ================================================= */
  /* FILTER */
  /* ================================================= */

  const filteredData = useMemo(() => {
    const data = rows.filter((r) => {
      const s = search.toLowerCase();
      return (
        (
          r.candidate_name?.toLowerCase().includes(s) ||
          r.company?.toLowerCase().includes(s) ||
          r.position?.toLowerCase().includes(s) ||
          r.recruiter_name?.toLowerCase().includes(s)
        ) &&
        (filterCompany ? r.company === filterCompany : true) &&
        (filterPosition ? r.position === filterPosition : true) &&
        (filterStatus ? String(r.status_joined || "").toUpperCase() === filterStatus.toUpperCase() : true)
      );
    });

    return data.sort((a, b) => b.s_no - a.s_no);
  }, [rows, search, filterCompany, filterPosition, filterStatus]);

  /* ================================================= */
  /* OPEN FORM (ADD OR EDIT) */
  /* ================================================= */

  const openForm = (row = null) => {
    const userData = JSON.parse(localStorage.getItem("USER") || "{}");
    const adminData = JSON.parse(localStorage.getItem("ADMIN") || "{}");
    const loggedUser = userData?.id ? userData : adminData;
    const currentUserName = `${loggedUser?.first_name || ""} ${loggedUser?.last_name || ""}`.trim() || "User";

    setIsManualAmount(false);

    if (row) {
      setEditingId(row.s_no);
      const ctc = row.ctc_offered || "";
      const pct = row.percentage || "8.33";
      const initialAmt = getAccurateAmount(row);

      const formattedDOJ = row.doj
        ? dayjs(row.doj).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      setFormData({
        s_no: row.s_no,
        candidate_id: row.candidate_id || null,
        candidate_name: row.candidate_name || "",
        phone: row.phone && row.phone !== "-" ? row.phone : "",
        company: row.company && row.company !== "-" ? row.company : "",
        position: row.position && row.position !== "-" ? row.position : "",
        designation: row.designation && row.designation !== "-" ? row.designation : "",
        doj: formattedDOJ,
        month: row.month || (formattedDOJ ? dayjs(formattedDOJ).format("MMMM") : dayjs().format("MMMM")),
        ctc_offered: ctc,
        percentage: pct,
        amount: initialAmt > 0 ? initialAmt : ((Number(ctc) * Number(pct)) / 100 || ""),
        status_joined: row.status_joined || "Joined",
        entered_by: row.recruiter_name || row.entered_by || currentUserName,
        user_id: row.user_id || loggedUser?.id || null,
      });
    } else {
      setEditingId(null);
      const today = dayjs();
      setFormData({
        ...emptyFormData,
        doj: today.format("YYYY-MM-DD"),
        month: today.format("MMMM"),
        entered_by: currentUserName,
        user_id: loggedUser?.id || null,
      });
    }
    setOpenModal(true);
  };

  /* ================================================= */
  /* DYNAMIC CALCULATION HANDLER */
  /* ================================================= */

  const updateCalculations = (newCtc, newPct, manualOverrideAmt = null) => {
    const ctcVal = Number(newCtc) || 0;
    const pctVal = Number(newPct) || 0;

    let calcAmt;
    if (manualOverrideAmt !== null) {
      calcAmt = manualOverrideAmt;
      setIsManualAmount(true);
    } else {
      calcAmt = Number(((ctcVal * pctVal) / 100).toFixed(2));
      setIsManualAmount(false);
    }

    setFormData((prev) => ({
      ...prev,
      ctc_offered: newCtc,
      percentage: newPct,
      amount: calcAmt,
    }));
  };

  const handleCtcChange = (value) => {
    updateCalculations(value, formData.percentage);
  };

  const handlePercentageChange = (value) => {
    updateCalculations(formData.ctc_offered, value);
  };

  const convertLakhsToInr = () => {
    const current = Number(formData.ctc_offered);
    if (current > 0 && current < 100) {
      const inrVal = current * 100000;
      updateCalculations(inrVal, formData.percentage);
    }
  };

  const resetToFormula = () => {
    setIsManualAmount(false);
    const ctcVal = Number(formData.ctc_offered) || 0;
    const pctVal = Number(formData.percentage) || 8.33;
    const amt = Number(((ctcVal * pctVal) / 100).toFixed(2));
    setFormData((prev) => ({ ...prev, amount: amt }));
  };

  /* ================================================= */
  /* SAVE (UPDATE OR SUBMIT) */
  /* ================================================= */

  const handleSave = async () => {
    if (!formData.candidate_name?.trim()) {
      return Swal.fire("Required", "Please enter the Candidate Name.", "warning");
    }

    let finalCtc = Number(formData.ctc_offered) || 0;
    // Auto-convert if entered as lakhs (< 100)
    if (finalCtc > 0 && finalCtc < 100) {
      finalCtc = finalCtc * 100000;
    }

    const finalPct = Number(formData.percentage) || 8.33;
    const formulaAmt = Number(((finalCtc * finalPct) / 100).toFixed(2));
    const finalAmount = isManualAmount && Number(formData.amount) > 0 ? Number(formData.amount) : formulaAmt;

    const payload = {
      candidate_id: formData.candidate_id || null,
      user_id: formData.user_id,
      candidate_name: formData.candidate_name.trim(),
      phone: formData.phone?.trim() || "",
      company: formData.company?.trim() || "",
      position: formData.position?.trim() || "",
      designation: formData.designation?.trim() || "",
      doj: formData.doj || null,
      month: formData.month || (formData.doj ? dayjs(formData.doj).format("MMMM") : ""),
      ctc_offered: finalCtc,
      percentage: finalPct,
      amount: finalAmount,
      status_joined: formData.status_joined || "Joined",
      entered_by: formData.entered_by || "User",
    };

    setSaving(true);
    try {
      let res;
      if (editingId) {
        res = await postData(`placementtracker/update/${editingId}`, payload);
      } else {
        res = await postData("placementtracker/submit", payload);
      }

      if (res?.status) {
        Swal.fire({
          icon: "success",
          title: editingId ? "Updated Successfully!" : "Added Successfully!",
          text: `Revenue calculated: ${formatINR(finalAmount)}`,
          timer: 2000,
          showConfirmButton: false,
        });
        setOpenModal(false);
        fetchAll();
      } else {
        Swal.fire("Error", res?.message || "Failed to save record.", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong while saving.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ================================================= */
  /* EXPORT */
  /* ================================================= */

  const handleExport = () => {
    if (!filteredData.length) {
      Swal.fire("Notice", "No data to export", "info");
      return;
    }

    const exportData = filteredData.map((r) => {
      const accurateAmount = getAccurateAmount(r);
      const obj = {};
      if (columns.candidate) obj["Candidate"] = r.candidate_name || "-";
      if (columns.company) obj["Company"] = r.company || "-";
      if (columns.position) obj["Position"] = r.position || "-";
      if (columns.recruiter) obj["Recruiter"] = r.recruiter_name || "-";
      if (columns.doj) obj["DOJ"] = r.doj ? dayjs(r.doj).format("DD-MM-YYYY") : "-";
      if (columns.ctc) obj["CTC Offered (INR)"] = Number(r.ctc_offered) || 0;
      if (columns.revenue) obj["Revenue (INR)"] = accurateAmount;
      if (columns.status) obj["Status"] = r.status_joined || "-";
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Joining Tracker");
    XLSX.writeFile(workbook, `Joining_Tracker_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  return (
    <div className="w-full space-y-4 min-h-[80vh]">
      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-extrabold text-gray-800 dark:text-slate-100 uppercase tracking-wider">
              Joining Tracker
            </h2>
            <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-950/50 px-2.5 py-0.5 text-xs font-bold text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
              {filteredData.length} Records
            </span>
          </div>
          <p className="text-[11px] font-medium text-gray-400 mt-0.5">
            Track candidates, offer CTCs, commission percentages, and verified revenue
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => openForm(null)}
            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md shadow-indigo-100 transition duration-200 active:scale-[0.98] flex items-center gap-1.5"
          >
            <FaPlus size={11} /> Add Joining
          </button>

          <button
            onClick={handleExport}
            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-md shadow-emerald-100 transition duration-200 active:scale-[0.98] flex items-center gap-1.5"
          >
            Export Excel 📄
          </button>

          <button
            onClick={fetchAll}
            title="Refresh Data"
            className="p-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-50 transition"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} size={14} />
          </button>

          {/* COLUMNS SELECTION BUTTON */}
          <div className="relative" ref={columnRef}>
            <button
              onClick={() => setOpenColumns((p) => !p)}
              className="px-3.5 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.98] flex items-center gap-2"
            >
              <FaCog /> Columns
            </button>

            {openColumns && (
              <div className="absolute right-0 mt-2 bg-white dark:bg-slate-800 p-4 border border-gray-100 dark:border-slate-700 rounded-2xl shadow-xl w-60 z-50 max-h-72 overflow-y-auto animate-fadeIn">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Visible Columns</p>
                {Object.keys(columns).map((c) => (
                  <label key={c} className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 items-center font-semibold text-gray-700 dark:text-gray-300 uppercase">
                    <input
                      type="checkbox"
                      checked={columns[c]}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      onChange={() => setColumns((prev) => ({ ...prev, [c]: !prev[c] }))}
                    />
                    {c}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div className="border border-gray-100 dark:border-slate-800 rounded-2xl overflow-visible bg-white dark:bg-slate-900 shadow-sm">
        <div className="border-b border-gray-100 dark:border-slate-800 bg-gray-50/30 dark:bg-slate-800/20 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search candidate, company, position..."
                className="h-10 w-full pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-200 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="h-10 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              className="h-10 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filterPosition}
              onChange={(e) => setFilterPosition(e.target.value)}
            >
              <option value="">All Positions</option>
              {positionOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <select
              className="h-10 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {(search || filterCompany || filterPosition || filterStatus) && (
              <button
                onClick={() => {
                  setSearch("");
                  setFilterCompany("");
                  setFilterPosition("");
                  setFilterStatus("");
                }}
                className="h-10 px-3 text-xs font-bold text-gray-500 hover:text-indigo-600 transition"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left">
                <th className="px-4 py-3.5 w-12 text-center">Sr</th>
                {columns.candidate && <th className="px-4 py-3.5">Candidate</th>}
                {columns.company && <th className="px-4 py-3.5">Company</th>}
                {columns.position && <th className="px-4 py-3.5">Position</th>}
                {columns.recruiter && <th className="px-4 py-3.5">Recruiter</th>}
                {columns.doj && <th className="px-4 py-3.5">DOJ</th>}
                {columns.ctc && <th className="px-4 py-3.5">CTC Offered</th>}
                {columns.revenue && <th className="px-4 py-3.5">Revenue / Amount</th>}
                {columns.status && <th className="px-4 py-3.5">Status</th>}
                <th className="px-4 py-3.5 w-[90px] text-center">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={10} className="p-12 text-center text-gray-400 font-semibold">
                    <FiRefreshCw className="inline-block animate-spin mr-2" size={16} />
                    Loading joining records...
                  </td>
                </tr>
              ) : filteredData.length ? (
                filteredData.map((r, i) => {
                  const accurateRevenue = getAccurateAmount(r);
                  const ctcNum = Number(r.ctc_offered) || 0;
                  const pctNum = Number(r.percentage) || 8.33;

                  return (
                    <tr
                      key={r.s_no}
                      className="hover:bg-indigo-50/20 dark:hover:bg-slate-800/40 transition duration-150 text-gray-700 dark:text-slate-300"
                    >
                      <td className="px-4 py-3.5 font-bold text-gray-400 text-center">
                        {i + 1}
                      </td>

                      {columns.candidate && (
                        <td className="px-4 py-3.5 font-bold text-gray-900 dark:text-slate-100 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-[11px]">
                              {r.candidate_name?.charAt(0)?.toUpperCase() || "C"}
                            </div>
                            <div>
                              <p className="font-bold">{r.candidate_name || "-"}</p>
                              {r.phone && r.phone !== "-" && (
                                <p className="text-[10px] font-normal text-gray-400">{r.phone}</p>
                              )}
                            </div>
                          </div>
                        </td>
                      )}

                      {columns.company && (
                        <td className="px-4 py-3.5 text-gray-700 dark:text-slate-300 whitespace-nowrap font-medium">
                          {r.company || "-"}
                        </td>
                      )}

                      {columns.position && (
                        <td className="px-4 py-3.5 font-semibold text-gray-800 dark:text-slate-200 whitespace-nowrap">
                          {r.position || "-"}
                        </td>
                      )}

                      {columns.recruiter && (
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 rounded-full font-bold text-[10px]">
                            {r.recruiter_name || r.entered_by || "-"}
                          </span>
                        </td>
                      )}

                      {columns.doj && (
                        <td className="px-4 py-3.5 whitespace-nowrap font-medium text-gray-600 dark:text-slate-400">
                          {r.doj ? dayjs(r.doj).format("DD-MM-YYYY") : "-"}
                        </td>
                      )}

                      {columns.ctc && (
                        <td className="px-4 py-3.5 whitespace-nowrap font-bold text-gray-800 dark:text-slate-200">
                          {formatINR(ctcNum)}
                        </td>
                      )}

                      {columns.revenue && (
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span className="font-extrabold text-emerald-600 dark:text-emerald-400 text-xs">
                              {formatINR(accurateRevenue)}
                            </span>
                            <span className="text-[9px] font-bold text-gray-400 bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                              {pctNum}%
                            </span>
                          </div>
                        </td>
                      )}

                      {columns.status && (
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                              r.status_joined === "Joined"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                                : r.status_joined === "Offered"
                                ? "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"
                                : r.status_joined === "Joining Pending"
                                ? "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
                                : r.status_joined === "Backout"
                                ? "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800"
                                : "bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-gray-300"
                            }`}
                          >
                            {r.status_joined || "Pending"}
                          </span>
                        </td>
                      )}

                      <td className="px-4 py-3.5 text-center">
                        <button
                          onClick={() => openForm(r)}
                          className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-600 text-indigo-600 hover:text-white border border-indigo-200 hover:border-indigo-600 text-[11px] font-bold rounded-xl transition duration-150 active:scale-[0.96] flex items-center justify-center gap-1 mx-auto"
                        >
                          <FiEdit2 size={12} /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="py-20 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50/80 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-500 text-xl mb-1">
                        💼
                      </div>
                      <p className="text-sm font-bold text-gray-700 dark:text-slate-200">No Joining Records Found</p>
                      <p className="text-xs text-gray-400 font-medium">There are no joining records matching your active filters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================= */}
      {/* COMPREHENSIVE EDIT / ADD MODAL */}
      {/* ================================================= */}
      {openModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-2xl shadow-2xl border border-gray-100 dark:border-slate-800 overflow-hidden animate-fadeIn p-6 sm:p-7 relative my-8">
            <button
              onClick={() => setOpenModal(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
            >
              <FiX size={20} />
            </button>

            <div className="flex items-center gap-3 mb-5 border-b border-gray-100 dark:border-slate-800 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold text-base shadow-md shadow-indigo-200">
                {editingId ? <FiEdit2 /> : <FaPlus />}
              </div>
              <div>
                <h3 className="text-base font-extrabold text-gray-900 dark:text-slate-100 uppercase tracking-wide">
                  {editingId ? "Edit Joining Record" : "Add New Joining"}
                </h3>
                <p className="text-xs text-gray-400">
                  Update candidate details, CTC offered, and commission percentage
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* CANDIDATE NAME */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Candidate Name *
                </label>
                <div className="relative">
                  <FiUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                  <input
                    type="text"
                    required
                    placeholder="Candidate full name"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.candidate_name}
                    onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })}
                  />
                </div>
              </div>

              {/* PHONE */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Phone Number
                </label>
                <div className="relative">
                  <FiPhone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                  <input
                    type="text"
                    placeholder="Phone number"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              {/* COMPANY */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Company
                </label>
                <div className="relative">
                  <FaBuilding className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                  <input
                    type="text"
                    list="company-list"
                    placeholder="Company name"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                  <datalist id="company-list">
                    {companyOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* POSITION */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Position / Role
                </label>
                <div className="relative">
                  <FiBriefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                  <input
                    type="text"
                    list="position-list"
                    placeholder="Position or designation"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  />
                  <datalist id="position-list">
                    {positionOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </datalist>
                </div>
              </div>

              {/* DATE OF JOINING (DOJ) */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Date of Joining (DOJ)
                </label>
                <div className="relative">
                  <FaCalendarAlt className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                  <input
                    type="date"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.doj}
                    onChange={(e) => {
                      const selected = e.target.value;
                      setFormData({
                        ...formData,
                        doj: selected,
                        month: selected ? dayjs(selected).format("MMMM") : formData.month,
                      });
                    }}
                  />
                </div>
              </div>

              {/* STATUS */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Joining Status
                </label>
                <select
                  className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                  value={formData.status_joined}
                  onChange={(e) => setFormData({ ...formData, status_joined: e.target.value })}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* RECRUITER */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">
                  Recruiter / Entered By
                </label>
                <div className="relative">
                  <FaUserTie className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                  <input
                    type="text"
                    placeholder="Recruiter name"
                    className="w-full h-10 pl-9 pr-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-gray-800 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.entered_by}
                    onChange={(e) => setFormData({ ...formData, entered_by: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* FINANCIAL CALCULATION SECTION */}
            <div className="mt-5 p-4 rounded-2xl bg-gradient-to-br from-indigo-50/70 via-slate-50 to-emerald-50/60 dark:from-slate-800 dark:via-slate-800/80 dark:to-emerald-950/30 border border-indigo-100 dark:border-slate-700">
              <h4 className="text-xs font-bold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FaMoneyBillWave className="text-indigo-600 dark:text-indigo-400" /> Revenue & Amount Calculation
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* CTC OFFERED */}
                <div>
                  <div className="flex items-center justify-between mb-1.5 ml-1">
                    <label className="text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      CTC Offered (₹ INR)
                    </label>
                    {Number(formData.ctc_offered) > 0 && Number(formData.ctc_offered) < 100 && (
                      <button
                        type="button"
                        onClick={convertLakhsToInr}
                        className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                      >
                        Convert {formData.ctc_offered} L to ₹{(Number(formData.ctc_offered) * 100000).toLocaleString('en-IN')}
                      </button>
                    )}
                  </div>
                  <input
                    type="number"
                    placeholder="e.g. 648000"
                    className="w-full h-10 px-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.ctc_offered}
                    onChange={(e) => handleCtcChange(e.target.value)}
                  />
                  {Number(formData.ctc_offered) >= 1000 && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 font-semibold ml-1">
                      {formatINR(formData.ctc_offered)}
                    </p>
                  )}
                </div>

                {/* COMMISSION PERCENTAGE */}
                <div>
                  <label className="text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider block mb-1.5 ml-1">
                    Commission / Fee %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="8.33"
                    className="w-full h-10 px-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition"
                    value={formData.percentage}
                    onChange={(e) => handlePercentageChange(e.target.value)}
                  />
                  {/* PRESET CHIPS */}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {standardPercentages.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => handlePercentageChange(item.value)}
                        className={`px-2 py-0.5 rounded-lg text-[9px] font-bold transition ${
                          Number(formData.percentage) === item.value
                            ? "bg-indigo-600 text-white"
                            : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-700 hover:border-indigo-400"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* CALCULATED REVENUE BANNER */}
              <div className="mt-4 pt-3 border-t border-indigo-100/80 dark:border-slate-700/80 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Calculated Placement Revenue:
                  </span>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-xl font-black text-emerald-600 dark:text-emerald-400">
                      {formatINR(formData.amount)}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                      ({formData.percentage || 0}% of {formatINR(formData.ctc_offered || 0)})
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isManualAmount && (
                    <button
                      type="button"
                      onClick={resetToFormula}
                      className="px-2.5 py-1 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-800 border border-indigo-200 dark:border-slate-700 rounded-lg hover:bg-indigo-50 transition"
                    >
                      Reset to Formula
                    </button>
                  )}
                  <div className="w-32">
                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                      Adjust / Override:
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full h-8 px-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-800 dark:text-slate-100"
                      value={formData.amount}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({ ...prev, amount: val }));
                        setIsManualAmount(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* MODAL ACTIONS */}
            <div className="flex justify-end gap-2.5 mt-6 pt-4 border-t border-gray-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setOpenModal(false)}
                className="px-4 py-2 border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-xl transition active:scale-[0.98]"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-150 transition active:scale-[0.98] flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <FiRefreshCw className="animate-spin" size={13} />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaCheckCircle size={13} />
                    {editingId ? "Update Record" : "Save Record"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}